/**
 * Pure derivation of crawlable {@link TriviaPage}s from committed {@link TriviaBoard}s
 * (#295). No I/O, no side effects — safe to call in the browser bundle, the
 * build-time prerender, the control plane, and tests. The web SEO layer and the
 * launch route both derive from the same functions here, so page URLs and the
 * board a page launches never drift.
 */

import type { Round } from '../domain';
import type { TriviaBoard, TriviaPage, TriviaPageItem } from './types';

/** Round display order — pages pool a topic's clues in board order. */
const ROUND_ORDER: Round[] = ['JEOPARDY', 'DOUBLE_JEOPARDY'];

/**
 * Normalize a category name to a topic key for pooling the *same* topic across
 * rounds (e.g. a `Science` column in both JEOPARDY and DOUBLE_JEOPARDY collapses to
 * one richer page). Case/punctuation/whitespace-insensitive.
 */
export function normalizeTopicKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/** Kebab-case a string into a URL-safe slug segment. */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

/**
 * The natural search phrase for a `(theme, topic)` pair — the human keyword a page
 * targets (e.g. `Sixth Grade Science`, `80s Movies Blockbusters`). Prefixes the theme
 * so the phrase reads like the search it targets, UNLESS the topic already carries the
 * theme words (so `Christmas` + `Christmas Movies` stays `Christmas Movies`, not
 * `Christmas Christmas Movies`). Titles, on-page copy, and slugs all derive from this,
 * so the URL and the visible keyword phrase never drift.
 */
export function triviaPhrase(theme: string, topic: string): string {
  const topicKey = normalizeTopicKey(topic);
  const themeKey = normalizeTopicKey(theme);
  return topicKey.includes(themeKey) ? topic : `${theme} ${topic}`;
}

/**
 * The SEO slug for a `(theme, topic)` pair: `/trivia/<slug>`. Kebab-cases the same
 * {@link triviaPhrase} the titles use, so two boards with a `Science` category don't
 * collide and the slug reads like the search it targets — `sixth-grade-science`.
 */
export function triviaSlug(theme: string, topic: string): string {
  return slugify(triviaPhrase(theme, topic));
}

function itemsFromClues(board: TriviaBoard, topicKey: string): TriviaPageItem[] {
  const items: TriviaPageItem[] = [];
  for (const round of ROUND_ORDER) {
    const packRound = board.pack.rounds.find((r) => r.round === round);
    if (!packRound) continue;
    for (const category of packRound.categories) {
      if (normalizeTopicKey(category.name) !== topicKey) continue;
      for (const clue of category.clues) {
        items.push({ prompt: clue.prompt, response: clue.response, value: clue.value });
      }
    }
  }
  return items.sort((a, b) => (a.value ?? 0) - (b.value ?? 0));
}

/**
 * Derive the pages for a single board: one page per distinct topic, pooling that
 * topic's clues across rounds, in board (round → column) order. Related links point
 * at the board's other topic pages. Slugs here are board-local; global uniqueness
 * across boards is enforced by {@link triviaPagesForBoards}.
 */
export function triviaPagesForBoard(board: TriviaBoard): TriviaPage[] {
  const order: string[] = [];
  const displayName = new Map<string, string>();
  for (const round of ROUND_ORDER) {
    const packRound = board.pack.rounds.find((r) => r.round === round);
    if (!packRound) continue;
    for (const category of packRound.categories) {
      const key = normalizeTopicKey(category.name);
      if (!displayName.has(key)) {
        displayName.set(key, category.name);
        order.push(key);
      }
    }
  }

  const slugFor = (key: string) => triviaSlug(board.theme, displayName.get(key) ?? key);

  return order.map((key) => {
    const topic = displayName.get(key) ?? key;
    const items = itemsFromClues(board, key);
    const related = order.filter((k) => k !== key).map(slugFor);
    const phrase = triviaPhrase(board.theme, topic);
    return {
      slug: slugFor(key),
      boardId: board.boardId,
      theme: board.theme,
      topic,
      // H1 — the keyword phrase the page targets, aligned with the <title>.
      title: `${phrase} Trivia`,
      // On-page intro paragraph — keyword-aligned with the title, then the play pitch.
      description:
        `${items.length} ${phrase} trivia questions with answers you can reveal ` +
        `below, then play the full ${board.theme} board as a live multiplayer buzzer ` +
        `game: one shared screen, phones as buzzers, no app to install.`,
      // <title> — front-loaded with the searchable phrase (the web SEO layer appends the
      // brand suffix from BRAND_NAME; kept brand-token-free here so `shared` stays
      // brand-agnostic).
      seoTitle: `${phrase} Trivia Questions & Answers`,
      // <meta description> — ~150-160 chars, keyword-bearing, with the play call to action.
      seoDescription:
        `${items.length} ${phrase} trivia questions with answers to reveal now — then ` +
        `play the full ${board.theme} board as a live multiplayer buzzer game. No app ` +
        `to install.`,
      items,
      related,
    };
  });
}

/**
 * All published pages across all boards, with globally-unique slugs (a later board
 * reusing a slug gets a `-2`, `-3`, … suffix). This is the authoritative page list
 * the prerender, sitemap, and routing iterate.
 */
export function triviaPagesForBoards(boards: TriviaBoard[]): TriviaPage[] {
  const seen = new Set<string>();
  const pages: TriviaPage[] = [];
  const remap = new Map<string, string>(); // original slug -> deduped slug (per board scope)

  for (const board of boards) {
    if (!board.published) continue;
    remap.clear();
    const boardPages = triviaPagesForBoard(board);
    for (const page of boardPages) {
      let slug = page.slug;
      let n = 2;
      while (seen.has(slug)) {
        slug = `${page.slug}-${n++}`;
      }
      seen.add(slug);
      remap.set(page.slug, slug);
      pages.push({ ...page, slug });
    }
    // Fix related links to the (possibly remapped) sibling slugs.
    for (const page of pages.slice(pages.length - boardPages.length)) {
      page.related = page.related.map((s) => remap.get(s) ?? s);
    }
  }
  return pages;
}

/** Look up a single published page by slug (undefined if not found/unpublished). */
export function triviaPageBySlug(
  boards: TriviaBoard[],
  slug: string,
): TriviaPage | undefined {
  return triviaPagesForBoards(boards).find((p) => p.slug === slug);
}

/** Published boards only (what the index groups by and the launch route accepts). */
export function publishedTriviaBoards(boards: TriviaBoard[]): TriviaBoard[] {
  return boards.filter((b) => b.published);
}

/** Resolve the launchable board for a page slug (what "Play this game" starts). */
export function triviaBoardForSlug(
  boards: TriviaBoard[],
  slug: string,
): TriviaBoard | undefined {
  const page = triviaPageBySlug(boards, slug);
  if (!page) return undefined;
  return boards.find((b) => b.boardId === page.boardId && b.published);
}
